import type { ReactNode } from 'react'
import { useI18n } from '@/shared/i18n/useT'

/**
 * The shell every right-hand grid panel shares — columns, chart, column options:
 * docked beside the grid from `sm` up, stacked full width below it on a phone,
 * and resized by dragging its left edge between `minWidth` and `maxWidth`.
 */
export function GridSidePanel({
  id,
  label,
  width,
  minWidth,
  maxWidth,
  onWidthChange,
  fullScreen,
  children,
}: {
  id: string
  label: string
  width: number
  minWidth: number
  maxWidth: number
  onWidthChange: (width: number) => void
  fullScreen: boolean
  children: ReactNode
}) {
  const { t } = useI18n()

  const startResize = (event: React.MouseEvent) => {
    event.preventDefault()
    const x0 = event.clientX
    const w0 = width
    const move = (e: MouseEvent) => {
      onWidthChange(Math.max(minWidth, Math.min(maxWidth, w0 - (e.clientX - x0))))
    }
    const release = () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', release)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', release)
  }

  return (
    <aside
      id={id}
      aria-label={label}
      style={{ width }}
      className={`relative z-20 flex shrink-0 flex-col border border-grid-line bg-surface shadow-card max-sm:static max-sm:!w-full max-sm:max-h-[calc(100dvh-12rem)] max-sm:rounded-lg ${fullScreen ? 'border-l-0' : 'rounded-r-lg border-l-0'}`}
    >
      {children}
      <div onMouseDown={startResize}
        className="absolute top-0 left-0 bottom-0 w-2 cursor-col-resize hover:bg-brand/30 active:bg-brand/50 transition-colors z-10"
        title={t('grid.dragResize')} />
    </aside>
  )
}
